import { Controller } from "@hotwired/stimulus"

// Snaps a native input value to the nearest multiple of the configured step.
// Attach with: data-controller="time-snap" data-time-snap-step-value="<minutes>"
// Supports <input type="time"> (HH:MM) and <input type="number"> (minutes).
// No-op when step <= 1.
export default class extends Controller {
  static values = { step: { type: Number, default: 0 } }

  connect() {
    this._bound = this._snap.bind(this)
    this.element.addEventListener("change", this._bound)
    this.element.addEventListener("blur", this._bound)
  }

  disconnect() {
    this.element.removeEventListener("change", this._bound)
    this.element.removeEventListener("blur", this._bound)
  }

  _snap() {
    const step = this.stepValue
    if (!step || step <= 1) return

    const value = this.element.value
    if (value === null || value === "") return

    if (this.element.type === "time") {
      if (!value.includes(":")) return
      const [h, m] = value.split(":").map((n) => parseInt(n, 10) || 0)
      const snapped = this._roundToStep(h * 60 + m, step, 1439)
      const newValue = this._formatHHMM(snapped)
      if (newValue !== value) {
        this.element.value = newValue
        this.element.dispatchEvent(new Event("input", { bubbles: true }))
      }
    } else if (this.element.type === "datetime-local") {
      const match = value.match(/^(\d{4}-\d{2}-\d{2}T)(\d{2}):(\d{2})(:\d{2})?$/)
      if (!match) return
      const [, datePart, hh, mm, seconds] = match
      const snapped = this._roundToStep(parseInt(hh, 10) * 60 + parseInt(mm, 10), step, 1439)
      const newValue = `${datePart}${this._formatHHMM(snapped)}${seconds || ""}`
      if (newValue !== value) {
        this.element.value = newValue
        this.element.dispatchEvent(new Event("input", { bubbles: true }))
      }
    } else {
      const parsed = parseInt(value, 10)
      if (Number.isNaN(parsed)) return
      const max = parseInt(this.element.max, 10) || 1440
      const snapped = this._roundToStep(parsed, step, max)
      if (String(snapped) !== value) {
        this.element.value = String(snapped)
        this.element.dispatchEvent(new Event("input", { bubbles: true }))
      }
    }
  }

  _roundToStep(total, step, max) {
    const rounded = Math.round(total / step) * step
    return Math.max(0, Math.min(max, rounded))
  }

  _formatHHMM(totalMinutes) {
    return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`
  }
}
